import { AVAILABLE_VIEW_KEYS } from '../types/schema'
import type { DashboardViewKey } from '../types/schema'
import type { ExtendedWorkspaceUseCase } from './workspaceModules'

export type TabLayout = 'team' | 'shop' | 'school'

export interface ViewsForSetupResult {
  enabledDashboardViews: DashboardViewKey[]
  defaultDashboardView: DashboardViewKey
}

/**
 * Returns the starting dashboard views and opening view for a new workspace
 * based on the chosen use case and audience (Plan §4 + OC7 + OC22).
 */
export function viewsForSetup(
  useCase?: ExtendedWorkspaceUseCase | string | null,
  audience?: 'SOLO' | 'SOLO_HELPERS' | 'TEAM' | string | null,
): ViewsForSetupResult {
  let defaultDashboardView: DashboardViewKey
  const switchedOff = new Set<DashboardViewKey>()

  switch (useCase) {
    case 'PERSONAL_GOALS':
      defaultDashboardView = 'JOURNEY'
      switchedOff.add('WARROOM')
      switchedOff.add('FLOWBAND')
      break
    case 'TEAM_PROJECTS':
      defaultDashboardView = 'TIMELINE'
      break
    case 'STUDY_PLANNING':
      defaultDashboardView = 'TIMELINE'
      switchedOff.add('WARROOM')
      switchedOff.add('FLOWBAND')
      break
    case 'CONTENT_PLANNING':
      defaultDashboardView = 'STRIPBOARD'
      break
    case 'STARTUP_BUILDING':
      defaultDashboardView = 'TIMELINE'
      break
    case 'FIELD_TEAM':
      defaultDashboardView = 'GRID'
      switchedOff.add('JOURNEY')
      switchedOff.add('CONSTELLATION')
      break
    case 'SHOP_STOCK':
      defaultDashboardView = 'GRID'
      switchedOff.add('JOURNEY')
      switchedOff.add('CONSTELLATION')
      switchedOff.add('WARROOM')
      break
    case 'EVENTS':
    case 'AGENCY':
    case 'SCHOOL':
    case 'TUITION_COACHING':
    case 'ACADEMY_TRAINING':
    case 'CORPORATE_TRAINING':
    case 'OTHER':
    default:
      defaultDashboardView = 'TIMELINE'
      break
  }

  // Audience rule (Plan §4): SOLO_HELPERS adds WARROOM and FLOWBAND to the off list,
  // and opens on GRID when the use case row says TIMELINE.
  if (audience === 'SOLO_HELPERS') {
    switchedOff.add('WARROOM')
    switchedOff.add('FLOWBAND')
    if (defaultDashboardView === 'TIMELINE') {
      defaultDashboardView = 'GRID'
    }
  }

  const enabledDashboardViews = AVAILABLE_VIEW_KEYS.filter((key) => !switchedOff.has(key))

  return {
    enabledDashboardViews,
    defaultDashboardView,
  }
}

/**
 * Maps a stored use-case key to its corresponding tab layout: 'team' | 'shop' | 'school'.
 * Empty / null / undefined returns null (Plan OC23, TB-D16).
 */
export function tabLayoutForUseCase(
  useCase?: string | null,
): TabLayout | null {
  if (!useCase || useCase.trim() === '') {
    return null
  }

  const trimmed = useCase.trim()

  if (trimmed === 'SHOP_STOCK') {
    return 'shop'
  }

  if (
    trimmed === 'SCHOOL' ||
    trimmed === 'TUITION_COACHING' ||
    trimmed === 'ACADEMY_TRAINING' ||
    trimmed === 'CORPORATE_TRAINING'
  ) {
    return 'school'
  }

  return 'team'
}
